import React, { useState, useRef, useEffect } from "react";
import { useUser } from "../../../../../contexts/userContext/userContext.js";
import { useModal } from "../../../../../contexts/modalContext/modalContext.js";
import { useContent } from "../../../../../contexts/contentContext/contentContext.js";
import { useToasts } from "react-toast-notifications";
import Button from "../../../../Elements/Button/Button.js";
import Toast from "../../../../Elements/Toast/Toast.js";
import { SizeMe } from "react-sizeme";
import { useMail } from "../../../../../contexts/mailContext/mailContext.js";
import ProfilePictureSelector from "./ProfilePictureSelector.js";
import LoaderContainer from "../../../../Reusable/LoaderContainer/LoaderContainer.js";
import "./ProfilePictureUpload.scss";
import { jumbomailImagesSizeLimit } from "utils/jumbomailSettings.js";
import Icon from "components/Elements/Icon/Icon.tsx";

const fileReader = new FileReader();

const UploadProfilePictureModal = ({ setDefault, selectedFileId }) => {
  const { CreateOrUpdateUserProfileReact, getUserProfileImage } = useUser();
  const { getFilesData, getMailStatus } = useMail();

  if (getMailStatus() !== "sent") {
    setDefault = true;
  }

  //canvas logic
  const { closeModal } = useModal();
  const [loading, setLoading] = useState(false);
  const { getTextByKey } = useContent();
  const { addToast } = useToasts();

  const [image, setImage] = useState(
    getUserProfileImage() ? getUserProfileImage() : null
  );
  const [imageIsFromUrl, setImageIsFromUrl] = useState(false);
  const [isImageFromPC, setIsImageFromPC] = useState(false);
  const [error, setError] = useState();
  const [fileId, setFileId] = useState(0);
  const inputRef = useRef();
  const canvasRef = useRef();
  const uploadedFileName = useRef();
  const [scale, setScale] = useState(1);

  fileReader.onload = function (event) {
    var image = new Image();
    image.onload = function () {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      const ratio = 1;
      canvas.width = image.width / ratio;
      canvas.height = image.height / ratio;
      context.drawImage(
        image,
        0,
        0,
        image.width,
        image.height,
        0,
        0,
        canvas.width,
        canvas.height
      );
      setImage(canvas.toDataURL());
    };
    image.src = event.target.result;
  };

  //side-effect for selecting image from mail
  useEffect(() => {
    if (fileId) {
      setIsImageFromPC(false);
      // getImageSrc(fileId);
    }
  }, [fileId]);

  //function will be fired after selecting photo from pc
  const handleNewImage = (e) => {
    if (e.target.files[0]) {
      setError(null);
      const allowedExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
      const fileExtension = e.target.files[0].name
        .split(".")
        .pop()
        .toLowerCase();
      const isAllowedExtension = allowedExtensions.includes(fileExtension);
      const fileSize = e.target.files[0].size;
      const validSize = jumbomailImagesSizeLimit["profilePicture"];
      const allowedSize = validSize >= fileSize;
      if (isAllowedExtension && allowedSize) {
        setIsImageFromPC(true);
        setScale(1);
        fileReader.readAsDataURL(e.target.files[0]);
        uploadedFileName.current = e.target.files[0].name;
      } else {
        setError(
          !isAllowedExtension
            ? getTextByKey("file-type-not-supported")
            : getTextByKey("embed-logo-validation-size-text")
        );
      }
    }
  };

  //clicking to "upload" button
  const handleClick = () => {
    inputRef.current.click();
  };

  // checking a size of selected image to
  const [imageSizes, setImageSizes] = useState({
    valid: true,
    width: "",
    height: "",
  });

  //saving image to s3 storage
  const handleSubmit = async () => {
    let response;
    setLoading(true);
    if (isImageFromPC) {
      const canvas = isImageFromPC
        ? canvasRef.current.getImage().toDataURL()
        : canvasRef.current.props.image;
      //  canvasRef.current.getImageScaledToCanvas().toDataURL();

      var proxyUrl =
        canvas.indexOf("data:image/png;base64,") > -1 || isImageFromPC
          ? ""
          : "https://proxy.jumbomail.me/";

      const blobResponse = await fetch(proxyUrl + canvas)
        .then((res) => res.blob())
        .then((blob) => {
          return blob;
        });

      const name =
        image && image.name
          ? image.name
          : fileId
          ? getFilesData()[fileId].Name
          : selectedFileId
          ? getFilesData()[selectedFileId].Name
          : uploadedFileName.current;
      response = await CreateOrUpdateUserProfileReact(
        blobResponse,
        name,
        fileId
      );
      if (response.status === "success") {
        closeModal();
        addToast(getTextByKey("upload-profile-picture-success"), {
          appearance: "success",
          autoDismissTimeout: 6000,
          autoDismiss: true,
        });
      } else {
        setError(response.data);
      }
    }

    setLoading(false);
    setFileId(0);
  };

  const cancelUpload = () => {
    setError(null);
    setImage(getUserProfileImage() ? getUserProfileImage() : null);
    setScale(1);
  };

  useEffect(() => {
    if (image) {
      const isFromUrl = image.startsWith("http");
      setImageIsFromUrl(isFromUrl);
    }
  }, [image]);

  return (
    <div className="profile-pic-cover-container">
      <input
        accept="image/*"
        className="click-to-action-upload-input"
        name="newImage"
        type="file"
        onChange={handleNewImage}
        ref={inputRef}
      ></input>
      <div className="profile-pic-cover-title title">
        {getTextByKey("upload-profile-picture-title")}
      </div>
      <div className="profile-pic-cover-content">
        <SizeMe>
          {({ size }) => (
            <div className="profile-picture-content-start">
              <ProfilePictureSelector
                scale={scale}
                setScale={setScale}
                image={image}
                size={size}
                handleClick={handleClick}
                cancelUpload={cancelUpload}
                handleNewImage={handleNewImage}
                inputRef={inputRef}
                ref={canvasRef}
                setImageSizes={setImageSizes}
                imageSizes={imageSizes}
                error={error}
              />
              <div
                className={`profile-pic-cover-footer ${image ? "" : "hide"}`}
              >
                {imageSizes.valid && !imageIsFromUrl ? (
                  <div className="profile-pic-cover-submit-button">
                    <LoaderContainer isLoading={loading}>
                      <Button
                        className={image ? null : "hidden"}
                        onClick={handleSubmit}
                      >
                        {getTextByKey("save")}
                      </Button>
                      <Button
                        blank
                        className={image ? null : "hidden"}
                        onClick={cancelUpload}
                      >
                        {getTextByKey("cancel")}
                      </Button>
                    </LoaderContainer>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </SizeMe>
        {!image && (
          <div className="profile-pic-cover-content-end">
            <div className="specific-profile-pic-cover-button">
              <Button className="new-button" onClick={handleClick}>
                <Icon name="camera" white />
                {getTextByKey("profile-upload-image-button")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadProfilePictureModal;
